import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import ToastContainer from "./components/ToastContainer";
import LiveOrderUpdates from "./components/LiveOrderUpdates";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AdminLogin from "./pages/AdminLogin";
import Register from "./pages/Register";
import UserDashboard from "./pages/user/Dashboard";
import PlaceOrder from "./pages/user/PlaceOrder";
import Track from "./pages/user/Track";
import OrderHistory from "./pages/user/OrderHistory";
import UserWallet from "./pages/user/Wallet";
import Subscription from "./pages/user/Subscription";
import UserProfile from "./pages/user/Profile";
import UserGuide from "./pages/user/UserGuide";
import PaymentCallback from "./pages/PaymentCallback";
import PublicOrderView from "./pages/PublicOrderView";
import PublicPaymentCallback from "./pages/PublicPaymentCallback";
import B2BDashboard from "./pages/business/B2BDashboard";
import FleetDashboard from "./pages/user/FleetDashboard";
import AgentDashboard from "./pages/user/AgentDashboard";
import BusinessLanding from "./pages/user/BusinessLanding";
import RiderDashboard from "./pages/rider/Dashboard";
import RiderWallet from "./pages/rider/RiderWallet";
import RiderHistory from "./pages/rider/RiderHistory";
import RiderProfile from "./pages/rider/Profile";
import RiderGuide from "./pages/rider/RiderGuide";
import RiderApproval from "./pages/admin/RiderApproval";
import PricingSettings from "./pages/admin/PricingSettings";
import AdminMapProviders from "./pages/admin/AdminMapProviders";
import AdminOrders from "./pages/admin/Orders";
import AdminIssues from "./pages/admin/Issues";
import AdminSupport from "./pages/admin/Support";
import AdminClaims from "./pages/admin/Claims";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminRevenue from "./pages/admin/AdminRevenue";
import AdminAdmins from "./pages/admin/AdminAdmins";
import AdminGuide from "./pages/admin/AdminGuide";
import AdminScreenPopups from "./pages/admin/AdminScreenPopups";
import "./App.css";

const ALL_ADMIN_ROLES = ["admin", "super_admin", "dispatch_manager", "finance_admin", "operations_manager", "support"];

const ROLE_HOME = {
  user: "/dashboard",
  rider: "/rider/dashboard",
  admin: "/admin/riders",
  super_admin: "/admin/riders",
  dispatch_manager: "/admin/orders",
  finance_admin: "/admin/revenue",
  operations_manager: "/admin/riders",
  support: "/admin/orders"
};

function RootRedirect() {
  const { account } = useAuth();
  if (!account) return <Landing />;
  return <Navigate to={ROLE_HOME[account.role] || "/dashboard"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
      <ToastContainer />
      <LiveOrderUpdates />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/register/:role" element={<Register />} />
          <Route path="/payment-callback" element={
            <ProtectedRoute allowedRoles={["user", "rider"]}><PaymentCallback /></ProtectedRoute>
          } />
          <Route path="/share/:token" element={<PublicOrderView />} />
          <Route path="/public-payment-callback" element={<PublicPaymentCallback />} />

          {/* User Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={["user"]}><UserDashboard /></ProtectedRoute>
          } />
          <Route path="/order" element={
            <ProtectedRoute allowedRoles={["user"]}><PlaceOrder /></ProtectedRoute>
          } />
          <Route path="/track/:orderId" element={
            <ProtectedRoute allowedRoles={["user"]}><Track /></ProtectedRoute>
          } />
          <Route path="/orders" element={
            <ProtectedRoute allowedRoles={["user"]}><OrderHistory /></ProtectedRoute>
          } />
          <Route path="/wallet" element={
            <ProtectedRoute allowedRoles={["user"]}><UserWallet /></ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute allowedRoles={["user"]}><Subscription /></ProtectedRoute>
          } />
          <Route path="/business" element={
            <ProtectedRoute allowedRoles={["user"]}><BusinessLanding /></ProtectedRoute>
          } />
          <Route path="/b2b" element={
            <ProtectedRoute allowedRoles={["user"]}><B2BDashboard /></ProtectedRoute>
          } />
          <Route path="/fleet" element={
            <ProtectedRoute allowedRoles={["user"]}><FleetDashboard /></ProtectedRoute>
          } />
          <Route path="/agent" element={
            <ProtectedRoute allowedRoles={["user"]}><AgentDashboard /></ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute allowedRoles={["user"]}><UserProfile /></ProtectedRoute>
          } />
          <Route path="/guide" element={
            <ProtectedRoute allowedRoles={["user"]}><UserGuide /></ProtectedRoute>
          } />

          {/* Rider Routes */}
          <Route path="/rider/dashboard" element={
            <ProtectedRoute allowedRoles={["rider"]}><RiderDashboard /></ProtectedRoute>
          } />
          <Route path="/rider/wallet" element={
            <ProtectedRoute allowedRoles={["rider"]}><RiderWallet /></ProtectedRoute>
          } />
          <Route path="/rider/history" element={
            <ProtectedRoute allowedRoles={["rider"]}><RiderHistory /></ProtectedRoute>
          } />
          <Route path="/rider/profile" element={
            <ProtectedRoute allowedRoles={["rider"]}><RiderProfile /></ProtectedRoute>
          } />
          <Route path="/rider/guide" element={
            <ProtectedRoute allowedRoles={["rider"]}><RiderGuide /></ProtectedRoute>
          } />

          {/* Admin Routes — "support" is a restricted admin sub-role (see
              models/Admin.js); every page it's let onto here still gates the
              underlying data by permission on the backend and hides tabs it
              lacks in AdminNav. Pricing and Admin Accounts stay admin-only —
              those two are never grantable to support. */}
          <Route path="/admin/riders" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><RiderApproval /></ProtectedRoute>
          } />
          <Route path="/admin/riders/:id" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><RiderApproval /></ProtectedRoute>
          } />
          <Route path="/admin/pricing" element={
            <ProtectedRoute allowedRoles={["admin", "super_admin"]}><PricingSettings /></ProtectedRoute>
          } />
          <Route path="/admin/map-providers" element={
            <ProtectedRoute allowedRoles={["admin", "super_admin"]}><AdminMapProviders /></ProtectedRoute>
          } />
          <Route path="/admin/revenue" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><AdminRevenue /></ProtectedRoute>
          } />
          <Route path="/admin/orders" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><AdminOrders /></ProtectedRoute>
          } />
          <Route path="/admin/issues" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><AdminIssues /></ProtectedRoute>
          } />
          <Route path="/admin/support" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><AdminSupport /></ProtectedRoute>
          } />
          <Route path="/admin/popups" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><AdminScreenPopups /></ProtectedRoute>
          } />
          <Route path="/admin/claims" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><AdminClaims /></ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><AdminUsers /></ProtectedRoute>
          } />
          <Route path="/admin/admins" element={
            <ProtectedRoute allowedRoles={["admin", "super_admin"]}><AdminAdmins /></ProtectedRoute>
          } />
          <Route path="/admin/guide" element={
            <ProtectedRoute allowedRoles={ALL_ADMIN_ROLES}><AdminGuide /></ProtectedRoute>
          } />

          {/* Catch-all Wildcard Route */}
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
